import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Alert,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from '@mui/material';
import {
  CheckCircle as ValidIcon,
  ErrorOutline as InvalidIcon,
  ContentPaste as PasteIcon,
} from '@mui/icons-material';
import { EasyCheckDialog, EasyCheckButton } from '../../common/Controls';
import { chequeService } from '../../../services/cheques/chequeService';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

const COLUMN_TYPES = [
  { value: 'beneficiary', label: 'Beneficiary' },
  { value: 'amount', label: 'Amount' },
  { value: 'date', label: 'Date' },
  { value: 'remarks', label: 'Remarks' },
  { value: 'ignore', label: 'Ignore' },
];

const DATE_FORMATS = [
  /^(\d{4})-(\d{2})-(\d{2})$/,
  /^(\d{4})\/(\d{2})\/(\d{2})$/,
  /^(\d{2})\/(\d{2})\/(\d{4})$/,  // dd/MM/yyyy
  /^(\d{2})-(\d{2})-(\d{4})$/,    // dd-MM-yyyy
  /^(\d{2})\.(\d{2})\.(\d{4})$/,  // dd.MM.yyyy
];

const parseAmount = (raw) => {
  if (!raw) return 0;
  const cleaned = String(raw).replace(/[^\d.\-]/g, '');
  const v = parseFloat(cleaned);
  return isNaN(v) ? 0 : v;
};

const normalizeDate = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  // Try yyyy-MM-dd directly
  let m = trimmed.match(DATE_FORMATS[0]);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = trimmed.match(DATE_FORMATS[1]);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // dd/MM/yyyy or dd-MM-yyyy or dd.MM.yyyy
  for (let i = 2; i < DATE_FORMATS.length; i++) {
    m = trimmed.match(DATE_FORMATS[i]);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  }
  // Try Date.parse fallback
  const d = new Date(trimmed);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return '';
};

const validateRow = (row) => {
  const errors = [];
  if (!row.beneficiary || row.beneficiary.length < 2) {
    errors.push('Beneficiary is required');
  } else if (row.beneficiary.length > 200) {
    errors.push('Beneficiary cannot exceed 200 characters');
  }
  if (!(row.amount > 0)) {
    errors.push('Amount must be greater than zero');
  }
  if (!row.date) {
    errors.push('Date is required or invalid');
  }
  if (row.remarks && row.remarks.length > 500) {
    errors.push('Remarks cannot exceed 500 characters');
  }
  return { isValid: errors.length === 0, errors };
};

const extractRowsClientSide = (rawRows, mapping) => {
  return rawRows.map((cells, rowIndex) => {
    const row = {
      rowIndex,
      beneficiary: '',
      amount: 0,
      date: '',
      remarks: '',
    };
    Object.entries(mapping).forEach(([colIdxStr, type]) => {
      const colIdx = parseInt(colIdxStr, 10);
      const cell = (cells[colIdx] || '').trim();
      switch (type) {
        case 'beneficiary': row.beneficiary = cell; break;
        case 'amount': row.amount = parseAmount(cell); break;
        case 'date': row.date = normalizeDate(cell); break;
        case 'remarks':
          row.remarks = row.remarks ? `${row.remarks} ${cell}`.trim() : cell;
          break;
        default: break;
      }
    });
    const { isValid, errors } = validateRow(row);
    return { ...row, isValid, errors };
  });
};

const PasteFromExcelDialog = ({ open, onClose, onPasteComplete, bankId }) => {
  const [stage, setStage] = useState('input'); // 'input' | 'preview'
  const [rawText, setRawText] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState('');

  // Server response artifacts
  const [rawRows, setRawRows] = useState([]); // 2D array
  const [columnMapping, setColumnMapping] = useState({}); // { "0": "amount", ... }
  const [confidence, setConfidence] = useState('high');
  const [bankCurrency, setBankCurrency] = useState('');
  const [serverRows, setServerRows] = useState([]); // initial parsed rows (with DB duplicate errors merged)

  const handleClose = () => {
    if (isParsing) return;
    setStage('input');
    setRawText('');
    setParseError('');
    setRawRows([]);
    setColumnMapping({});
    setConfidence('high');
    setServerRows([]);
    setBankCurrency('');
    onClose();
  };

  const handleParse = useCallback(async () => {
    if (!rawText.trim()) {
      setParseError('Please paste some rows first.');
      return;
    }
    if (!bankId) {
      setParseError('Please select a bank account before parsing.');
      return;
    }
    setIsParsing(true);
    setParseError('');
    try {
      const result = await chequeService.parsePaste({ rawText, bankAccountId: bankId });
      if (result?.success) {
        const data = result.data || {};
        setRawRows(Array.isArray(data.RawRows || data.rawRows) ? (data.RawRows || data.rawRows) : []);
        setColumnMapping(data.ColumnMapping || data.columnMapping || {});
        setConfidence(data.Confidence || data.confidence || 'high');
        setBankCurrency(data.BankCurrency || data.bankCurrency || '');
        const rowsList = data.Rows || data.rows || [];
        setServerRows(rowsList.map(r => ({
          rowIndex: r.RowIndex ?? r.rowIndex,
          beneficiary: r.Beneficiary ?? r.beneficiary ?? '',
          amount: r.Amount ?? r.amount ?? 0,
          date: r.Date ?? r.date ?? '',
          remarks: r.Remarks ?? r.remarks ?? '',
          isValid: r.IsValid ?? r.isValid ?? false,
          errors: r.Errors ?? r.errors ?? [],
        })));
        setStage('preview');
      } else {
        setParseError(result?.message || 'Failed to parse paste.');
      }
    } catch (err) {
      setParseError(err?.message || 'Failed to parse paste.');
    } finally {
      setIsParsing(false);
    }
  }, [rawText, bankId]);

  // Did the user manually override the column mapping?
  const [overrideMapping, setOverrideMapping] = useState(null);
  const activeMapping = overrideMapping || columnMapping;

  // When mapping is overridden, re-extract client-side. We lose DB-duplicate errors
  // (a re-parse would catch them again at create time).
  const previewRows = useMemo(() => {
    if (!overrideMapping) return serverRows;
    return extractRowsClientSide(rawRows, overrideMapping);
  }, [overrideMapping, serverRows, rawRows]);

  const handleColumnTypeChange = (colIdx, newType) => {
    const next = { ...activeMapping };
    if (newType === 'ignore') {
      delete next[String(colIdx)];
    } else {
      next[String(colIdx)] = newType;
    }
    setOverrideMapping(next);
  };

  const validCount = previewRows.filter(r => r.isValid).length;
  const totalCount = previewRows.length;
  const totalAmount = previewRows
    .filter(r => r.isValid)
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const handleConfirm = () => {
    const validRows = previewRows.filter(r => r.isValid);
    if (validRows.length === 0) return;
    // Cheque numbers intentionally NOT set — backend auto-generates next sequential at create time.
    const out = validRows.map(r => ({
      chequeNumber: '',
      beneficiaryName: r.beneficiary,
      chqAmount: Number(r.amount) || 0,
      chqDate: r.date,
      remarks: r.remarks || '',
      source: 'paste',
    }));
    onPasteComplete(out);
    handleClose();
  };

  const colCount = rawRows.length > 0 ? rawRows[0].length : 0;

  const renderInputStage = () => (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 0 }}>
        Copy rows from Excel and paste below. Columns can be in any order.
        <br />
        <strong>Required (3 columns):</strong> Beneficiary, Date, Amount. <strong>Remarks</strong> optional as a 4th column.
        <br />
        Cheque numbers will be <strong>auto-generated</strong> — do not include them in the paste.
      </Alert>

      <TextField
        multiline
        rows={10}
        fullWidth
        value={rawText}
        onChange={(e) => setRawText(e.target.value)}
        placeholder={'John Smith\t2026-03-01\t5000\tMonthly\nJane Doe\t2026-03-02\t7500.50\t'}
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
        }}
      />

      {parseError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>{parseError}</Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        Maximum 100 rows. Tab-separated columns.
      </Typography>
    </Stack>
  );

  const renderPreviewStage = () => (
    <Stack spacing={2}>
      {confidence === 'ambiguous' && !overrideMapping && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          We couldn't confidently detect every column — please verify the dropdowns below before confirming.
        </Alert>
      )}

      {/* Column-type override row */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, backgroundColor: '#F7F9FC' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Detected columns (change any if wrong):
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Array.from({ length: colCount }).map((_, colIdx) => {
            const currentType = activeMapping[String(colIdx)] || 'ignore';
            return (
              <Box key={colIdx} sx={{ minWidth: 150 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Col {colIdx + 1}
                </Typography>
                <Select
                  value={currentType}
                  onChange={(e) => handleColumnTypeChange(colIdx, e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ borderRadius: 0, fontSize: '0.875rem' }}
                >
                  {COLUMN_TYPES.map(opt => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.875rem' }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            );
          })}
        </Stack>
      </Paper>

      {/* Rows preview */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50, fontWeight: 600 }}>#</TableCell>
              <TableCell sx={{ width: 60, fontWeight: 600 }}></TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Beneficiary</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row, idx) => (
              <TableRow
                key={idx}
                sx={{ backgroundColor: row.isValid ? 'inherit' : 'rgba(211, 47, 47, 0.04)' }}
              >
                <TableCell>{idx + 1}</TableCell>
                <TableCell>
                  {row.isValid ? (
                    <ValidIcon fontSize="small" sx={{ color: 'success.main' }} />
                  ) : (
                    <Tooltip title={row.errors.join('; ')} arrow>
                      <InvalidIcon fontSize="small" sx={{ color: 'error.main', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{row.beneficiary || <em style={{ color: '#999' }}>—</em>}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {row.date || <em style={{ color: '#999' }}>—</em>}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {row.amount > 0 ? formatCurrencyAmount(row.amount, bankCurrency, { withSymbol: false }) : '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>{row.remarks}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer summary */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
        <Chip
          label={`Valid: ${validCount} / ${totalCount}`}
          color={validCount === totalCount ? 'success' : 'warning'}
          size="small"
          variant="outlined"
        />
        <Chip
          label={`Total: ${bankCurrency || ''} ${formatCurrencyAmount(totalAmount, bankCurrency, { withSymbol: false })}`.trim()}
          size="small"
          variant="outlined"
        />
        {validCount < totalCount && (
          <Typography variant="caption" color="text.secondary">
            Only valid rows will be added to the staging grid.
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  const dialogActions = stage === 'input' ? (
    <>
      <EasyCheckButton buttonType="form" variant="outlined" onClick={handleClose} disabled={isParsing}>
        Cancel
      </EasyCheckButton>
      <EasyCheckButton
        buttonType="action"
        variant="contained"
        onClick={handleParse}
        disabled={isParsing || !rawText.trim() || !bankId}
        startIcon={isParsing ? <CircularProgress size={18} color="inherit" /> : <PasteIcon />}
      >
        {isParsing ? 'Parsing…' : 'Parse Cheques'}
      </EasyCheckButton>
    </>
  ) : (
    <>
      <EasyCheckButton
        buttonType="form"
        variant="outlined"
        onClick={() => {
          setStage('input');
          setOverrideMapping(null);
        }}
      >
        ← Back to Edit
      </EasyCheckButton>
      <EasyCheckButton
        buttonType="action"
        variant="contained"
        color="success"
        onClick={handleConfirm}
        disabled={validCount === 0}
      >
        Confirm & Add to Grid ({validCount})
      </EasyCheckButton>
    </>
  );

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={stage === 'input' ? 'Paste from Excel' : 'Preview Parsed Cheques'}
      titleIcon={<PasteIcon />}
      variant="large"
      maxWidth="lg"
      fullWidth
      actions={dialogActions}
      disableBackdropClick
    >
      {stage === 'input' ? renderInputStage() : renderPreviewStage()}
    </EasyCheckDialog>
  );
};

export default PasteFromExcelDialog;
